/**
 * XeroSyncStatusIndicator Component
 *
 * Displays the Xero synchronisation status for credit notes with appropriate
 * icons, colours, and tooltips. Includes retry functionality for failed syncs.
 *
 * User Story: US-REC-001 - Invoice Reconciliation
 */

import {
  CheckCircleIcon,
  Loader2Icon,
  AlertTriangleIcon,
  RefreshCwIcon,
  MinusCircleIcon
} from 'lucide-react'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '@/components/ui/tooltip'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

/**
 * Sync status configuration
 */
const STATUS_CONFIG = {
  synced: {
    icon: CheckCircleIcon,
    colour: 'text-green-600',
    bgColour: 'bg-green-50',
    label: 'Synced to Xero',
    description: 'Successfully synced to Xero'
  },
  pending: {
    icon: Loader2Icon,
    colour: 'text-blue-600',
    bgColour: 'bg-blue-50',
    label: 'Syncing...',
    description: 'Syncing to Xero...',
    animate: true
  },
  failed: {
    icon: AlertTriangleIcon,
    colour: 'text-amber-600',
    bgColour: 'bg-amber-50',
    label: 'Sync failed',
    description: 'Failed to sync to Xero'
  },
  not_synced: {
    icon: MinusCircleIcon,
    colour: 'text-gray-400',
    bgColour: 'bg-gray-50',
    label: 'Not synced',
    description: 'Not yet synced to Xero'
  },
  not_applicable: {
    icon: null, // Don't show anything for not_applicable
    colour: 'text-gray-400',
    bgColour: 'bg-gray-50',
    label: 'Local only',
    description: 'Not synced (local invoice)'
  }
}

/**
 * XeroSyncStatusIndicator displays sync status with optional retry
 * @param {Object} props - Component props
 * @param {string} props.status - Sync status (synced, pending, failed, not_synced, not_applicable)
 * @param {string} props.syncError - Error message if status is failed
 * @param {string} props.xeroNumber - Xero credit note number (if synced)
 * @param {function} props.onRetry - Callback for retry button (only shown when failed)
 * @param {boolean} props.retrying - Whether retry is in progress
 * @param {string} props.size - Icon size: 'sm' | 'md' (default: 'sm')
 * @param {boolean} props.showLabel - Whether to show status label text
 * @param {string} props.className - Additional CSS classes
 */
export function XeroSyncStatusIndicator({
  status,
  syncError,
  xeroNumber,
  onRetry,
  retrying = false,
  size = 'sm',
  showLabel = false,
  className
}) {
  const config = STATUS_CONFIG[status] || STATUS_CONFIG.not_synced

  // Don't render anything for not_applicable status (legacy invoices)
  if (status === 'not_applicable' && !showLabel) {
    return null
  }

  const IconComponent = config.icon
  const iconSize = size === 'sm' ? 'h-4 w-4' : 'h-5 w-5'

  // Build tooltip content
  const tooltipContent = () => {
    if (status === 'synced' && xeroNumber) {
      return (
        <div className="text-xs">
          <div className="font-medium">{config.label}</div>
          <div className="text-gray-500">Xero Ref: {xeroNumber}</div>
        </div>
      )
    }

    if (status === 'failed' && syncError) {
      return (
        <div className="text-xs max-w-xs">
          <div className="font-medium text-amber-600">{config.label}</div>
          <div className="text-gray-500 mt-1">{syncError}</div>
        </div>
      )
    }

    return <span className="text-xs">{config.description}</span>
  }

  // Handle retry click
  const handleRetryClick = (e) => {
    e.stopPropagation()
    if (onRetry && !retrying) {
      onRetry()
    }
  }

  // If no icon (not_applicable without showLabel), return null
  if (!IconComponent && !showLabel) {
    return null
  }

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            className={cn(
              'inline-flex items-center gap-1.5',
              className
            )}
          >
            {IconComponent && (
              <IconComponent
                className={cn(
                  iconSize,
                  config.colour,
                  config.animate && 'animate-spin'
                )}
                aria-hidden="true"
              />
            )}

            {showLabel && (
              <span className={cn('text-xs', config.colour)}>
                {config.label}
              </span>
            )}

            {/* Retry button for failed syncs */}
            {status === 'failed' && onRetry && (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-5 w-5 ml-1"
                onClick={handleRetryClick}
                disabled={retrying}
                aria-label="Retry sync to Xero"
              >
                <RefreshCwIcon
                  className={cn(
                    'h-3 w-3',
                    retrying ? 'animate-spin text-blue-600' : 'text-amber-600 hover:text-amber-700'
                  )}
                />
              </Button>
            )}
          </span>
        </TooltipTrigger>
        <TooltipContent side="top">
          {tooltipContent()}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}

export default XeroSyncStatusIndicator
